import { SizeMorphDirective } from '../_card-base/size-morph.directive';
import { DatePipe, DecimalPipe } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    OnInit,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import {
    DocumentController,
    DocumentItemPartial,
    FavoriteFieldEntity,
} from '@domain';
import { environment } from '@environments';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { of, switchMap, tap } from 'rxjs';
import { DocumentFieldsController } from 'src/app/domain/controllers/document/document-fields.controller';
import { TrashDocumentItemEntity } from 'src/app/domain/entities/trash-document-item.entity';
import { RaEditImageComponent } from 'src/app/shared/components/ra-edit-image/ra-edit-image.component';
import { RaImageFullScreenModel } from 'src/app/shared/components/ra-images-full-screen/model/ra-image-full-screen.model';
import { RaImagesFullScreenComponent } from 'src/app/shared/components/ra-images-full-screen/ra-images-full-screen.component';
import { UploadProgressService } from 'src/app/shared/services/upload-progress.service';
import { DigitalOceanStorage } from 'src/app/shared/utils/digital-ocean/digital-ocean-storage';
import { MediaUtils } from 'src/app/shared/utils/media.utils';
import { deltaToPlainText } from 'src/app/shared/utils/delta.util';
import { EditDataService } from '../../../services/_edit-data.service';
import { EditItemsService } from '../../../services/edit-items.service';
import { EditService } from '../../../services/edit-service';
import { ModalImageDateComponent } from '../../modals/modal-image-date/modal-image-date.component';
import { CardBaseComponent } from '../_card-base/card-base.component';
import { CardDescriptionAreaComponent } from '../_card-base/card-description-area.component';
import { CardTypes } from '../_card-base/enums/card-types.enum';
import { imageNumberForItem } from '../../../utils/image-number.util';
import { buildSectionGallery } from '../../../utils/section-gallery.util';
import { DynamicActionModel } from '../_card-base/model/dynamic-action.model';
import { RaEditTooltipDirective } from '../../../directives/ra-edit-tooltip/ra-edit-tooltip.directive';

import { resolveSingleImageSize } from '../../../../../pdf-renderer/image-size';
import {
    CaptionStyle,
    resolveCaptionSide,
    resolveCaptionStyle,
    sideImageSize,
} from '../../../../../pdf-renderer/caption-style';
import { MediaLookChipComponent } from '../_card-base/media-look-chip/media-look-chip.component';
import {
    hasAnyKey,
    withCaptionSide,
    withCaptionStyle,
    withMediaFill,
    withOverride,
    withoutKeys,
} from '../_card-base/item-look.util';
import { MEDIA_FILL_DEFAULT, resolveMediaFill } from '../../../../../media-fill-options';
import { ImageSize, sameImageSize, snapImageSize, snapSideImageSize } from './image-size.util';

@Component({
    selector: 'image-card',
    templateUrl: 'image-card.component.html',
    styleUrl: 'image-card.component.scss',
    imports: [
        SizeMorphDirective,
        MediaLookChipComponent,
        CardBaseComponent,
        CardDescriptionAreaComponent,
        DatePipe,
        DecimalPipe,
        ModalImageDateComponent,
        RaEditImageComponent,
        RaImagesFullScreenComponent,
        RaEditTooltipDirective,
        TranslatePipe,
    ],
})
export class ImageCardComponent implements OnInit {
    public field = input<DocumentItemPartial>();
    public fontFamily = input<string>(environment.DEFAULT_FONT_FAMILY);
    public favoriteField = input<FavoriteFieldEntity>();
    public trashField = input<TrashDocumentItemEntity>();
    public type = input<CardTypes>(CardTypes.DEFAULT);
    public selected = input<boolean>(false);
    public imageActions: DynamicActionModel[] = [];
    public showEditImage = signal(false);
    public showFullScreen = signal(false);
    /** Galeria montada ao abrir a tela cheia (todas as imagens da seção). */
    public galleryImages = signal<RaImageFullScreenModel[]>([]);
    public galleryStartIndex = signal(0);
    /** Painel "Foto"/"Grade"/"Vídeo" do rail: é ele que a prévia abre. */
    private _lookChip = viewChild(MediaLookChipComponent);
    /** Modal de troca da data da imagem (paridade com o app). */
    public showDateModal = signal(false);

    // Número/data no card, espelhando preview e PDF: config por-item tem
    // precedência sobre a config do documento (mesma regra do image-data).
    public showImageNumber = computed<boolean>(
        () =>
            this.field()?.configs?.showImageNumber ??
            this.editDataService.document()?.config?.showImageNumber ??
            false
    );
    public showImageDate = computed<boolean>(
        () =>
            this.field()?.configs?.showImageDate ??
            this.editDataService.document()?.config?.showImageDate ??
            false
    );
    /** Nº desta imagem no documento (null fora do editor: lixeira/favoritos). */
    public imageNumber = computed<number | null>(() =>
        imageNumberForItem(this.editDataService.document(), this.field()?._id)
    );
    public cardTypes = CardTypes;

    // * [Injects]
    private _mediaUtils = inject(MediaUtils);
    public editService = inject(EditService);
    public editDataService = inject(EditDataService);
    public editItemsService = inject(EditItemsService);
    private _documentController = inject(DocumentController);
    private _digitalOceanStorage = inject(DigitalOceanStorage);
    private _uploadProgressService = inject(UploadProgressService);
    private _documentFieldsController = inject(DocumentFieldsController);
    private _translate = inject(TranslateService);

    constructor() {
        /*
         * A prévia pede as configurações deste item pelo menu que ela mostra em cima dele.
         * Abre o MESMO painel do botão do rail (cada card tem o seu), então o pedido chega
         * por um signal e o card dono abre o painel dele, e limpa o pedido, senão voltar
         * para a seção mais tarde reabriria o painel do nada.
         *
         * O painel só existe depois que a view do card monta: até lá o pedido espera, e o
         * effect roda de novo quando o `viewChild` resolve.
         */
        effect(() => {
            const requested = this.editDataService.itemConfigRequestId();
            if (!requested || requested !== this.field()?._id) return;

            const lookChip = this._lookChip();
            if (!lookChip) return;

            untracked(() => {
                lookChip.open();
                this.editDataService.clearItemConfigRequest();
            });
        });

        /*
         * Largura digitada no menu "Foto" que ainda não salvou (o chip espera 400ms) e o card
         * saiu da tela (outra seção, campo removido): salva daqui. O chip não consegue, os
         * outputs dele já estão desligados quando o destroy dele roda.
         */
        inject(DestroyRef).onDestroy(() => {
            const live = this._liveSize();
            if (live && !this.resizing()) this.chooseSize(live);
        });
    }

    ngOnInit(): void {
        this.buildDynamicActions();
    }

    private buildDynamicActions(): void {
        const tr = this._translate;
        this.imageActions = [
            // Todas no grupo "Foto" do menu ⋯ (o rail da mídia tem cinco botões fixos). As
            // configurações (tamanho, enquadramento, número e data) são o botão de paleta.
            {
                id: 'edit',
                icon: 'edit-2',
                tooltip: tr.instant('document.edit.imageCard.editImage'),
                action: () => this.showEditImage.set(true),
            },
            {
                id: 'swap',
                icon: 'arrow-swap-horizontal',
                tooltip: tr.instant('document.edit.imageCard.swapImage'),
                action: () => this.changeImage(),
            },
            {
                id: 'date',
                icon: 'calendar',
                tooltip: tr.instant('document.edit.imageCard.changeDate'),
                action: () => this.showDateModal.set(true),
            },
            {
                id: 'add',
                icon: 'add',
                tooltip: tr.instant('document.edit.imageCard.addMoreImages'),
                action: () =>
                    this.editItemsService.addImageToField(this.getCurrentField()._id).subscribe(),
            },
        ];
    }

    /**
     * Salva as configurações só deste card (auto-save do modal).
     * @param configs Override do item — o que não estiver aqui segue o documento
     */
    /** Confirmação do modal de data: aplica otimista e persiste. */
    public onSaveImageDate(date: Date): void {
        this.showDateModal.set(false);
        this.editItemsService.changeImageDate(this.getCurrentField()._id, date);
    }

    /** Tamanho ao vivo (arraste da alça ou − / + e digitação no menu): só na tela. */
    private _liveSize = signal<ImageSize | null>(null);
    /** Arrastando pela alça: mostra o selo com o tamanho. */
    public resizing = signal(false);

    /** Tamanho salvo: o primeiro nível que decide (card, documento), como no PDF. */
    public savedSize = computed<ImageSize>(() =>
        resolveSingleImageSize([
            this.getCurrentField()?.configs,
            this.editDataService.document()?.config,
        ])
    );

    /**
     * Chave do tamanho SALVO, para a troca de tamanho animar (ver SizeMorphDirective). O
     * tamanho ao vivo (arrasto dos cantos, régua do painel) fica fora de propósito: ali a foto
     * segue a mão, sem animação.
     */
    public sizeKey = computed(() => {
        const size = this.savedSize();
        return `${size.grid}:${size.width ?? ''}:${this.captionSide() ? 'side' : ''}`;
    });

    /** Tamanho escolhido: o ao vivo, senão o salvo (é o que o menu "Foto" marca). */
    public displaySize = computed<ImageSize>(() => this._liveSize() ?? this.savedSize());

    /** Estilo da legenda efetivo (card, documento), como no PDF. */
    public captionStyle = computed<CaptionStyle>(() =>
        resolveCaptionStyle([
            this.getCurrentField()?.configs,
            this.editDataService.document()?.config,
        ])
    );

    /** Descrição ao lado efetiva (card, documento). */
    public captionSide = computed<boolean>(() =>
        resolveCaptionSide([this.getCurrentField()?.configs, this.editDataService.document()?.config])
    );

    /** Tamanho desenhado: com a descrição ao lado, a largura livre vai até 60% (sideImageSize). */
    public shownSize = computed<ImageSize>(() =>
        this.captionSide() ? sideImageSize(this.displaySize()) : this.displaySize()
    );

    /**
     * Largura da coluna da foto ao lado da legenda, as mesmas do PDF (vão de 1rem): grande com
     * 60%, média com metade, pequena com um terço.
     */
    public sideWidth = computed<string>(() => {
        const size = this.shownSize();
        if (size.width !== null) return `${size.width}%`;
        if (size.grid === 1) return 'calc((100% - 1rem) * 0.6)';
        return size.grid === 3 ? 'calc((100% - 2rem) / 3)' : 'calc((100% - 1rem) / 2)';
    });

    /** Rótulo do tamanho durante o arraste: o nome do tamanho pronto ou a porcentagem. */
    public resizeLabel = computed(() => {
        const size = this.displaySize();
        return size.width !== null
            ? `${size.width}%`
            : this._translate.instant(`document.edit.documentConfig.singleImageSize${size.grid}`);
    });

    /** "Imagem inteira" (sem cortes) efetivo: override do card, senão a config do documento. */
    public isFullImage(): boolean {
        return (
            this.getCurrentField()?.configs?.fullImage ??
            this.editDataService.document()?.config?.fullImage ??
            false
        );
    }

    /** Tamanho ao vivo vindo do menu "Tamanho" do rail (null = volta ao salvo). */
    public previewSize(size: ImageSize | null): void {
        this._liveSize.set(size);
    }

    /**
     * Grava o tamanho no card. Igual ao que o documento já dá vira herança (as duas chaves
     * saem), como o modal faz: senão o card "seguiria" um valor que por acaso é o mesmo, e
     * mudar o documento depois não o alcançaria. A largura livre leva junto o pronto mais
     * perto, que é o que o app (sem largura livre) mostra. As chaves vão como `null`
     * porque o update-config do item MESCLA: omitidas, ficariam gravadas no servidor.
     */
    public chooseSize(size: ImageSize): void {
        const field = this.getCurrentField();
        if (!field?._id || sameImageSize(size, this.savedSize())) return;

        const inherited = sameImageSize(
            size,
            resolveSingleImageSize([this.editDataService.document()?.config])
        );

        this.editItemsService.updateItemConfig(field._id, {
            ...(field.configs ?? {}),
            singleImageGrid: inherited ? null : size.grid,
            imageWidth: inherited ? null : size.width,
        });
    }

    /** Chaves do menu "Foto" que o card pode sobrescrever. */
    private static readonly LOOK_KEYS = [
        'singleImageGrid',
        'imageWidth',
        'fullImage',
        'showImageNumber',
        'showImageDate',
        'captionStyle',
        'captionPosition',
        'imageFillColor',
    ] as const;

    /** Cor do fundo ao vivo, enquanto o seletor livre do painel é arrastado. */
    private _liveFill = signal<string | null>(null);

    /** Cor do fundo efetiva (card, documento), como no PDF; a ao vivo por cima. */
    public fillColor = computed(
        () =>
            this._liveFill() ??
            resolveMediaFill([
                this.getCurrentField()?.configs,
                this.editDataService.document()?.config,
            ])
    );

    /**
     * A var `--media-fill` do card: só com cor escolhida. Sem ela a sobra fica no cinza do
     * tema (--backgroundSecondary), que no tema claro é o mesmo cinza padrão do PDF.
     */
    public fillStyle = computed(() => {
        const color = this.fillColor();
        return color === MEDIA_FILL_DEFAULT ? null : color;
    });

    /** Cor ao vivo vinda do seletor livre do painel (null = volta à salva). */
    public previewFill(color: string | null): void {
        this._liveFill.set(color);
    }

    /** Cor do fundo pelo painel (igual à do documento vira herança). */
    public setFill(color: string): void {
        const field = this.getCurrentField();
        if (!field?._id) return;

        this.editItemsService.updateItemConfig(
            field._id,
            withMediaFill(field.configs, color, this.editDataService.document()?.config)
        );
    }

    /** Algo do menu "Foto" está sobrescrito neste card (mostra "Voltar ao padrão"). */
    public lookOverridden = computed(() =>
        hasAnyKey(this.getCurrentField()?.configs, ImageCardComponent.LOOK_KEYS)
    );

    /** Enquadramento, número e data pelo menu "Foto" (igual ao documento vira herança). */
    public setLook(key: 'fullImage' | 'showImageNumber' | 'showImageDate', value: boolean): void {
        const field = this.getCurrentField();
        if (!field?._id) return;

        const inherited = this.editDataService.document()?.config?.[key] ?? false;
        this.editItemsService.updateItemConfig(
            field._id,
            withOverride(field.configs, key, value, inherited)
        );
    }

    /** Estilo da legenda pelo menu "Foto" (igual ao documento vira herança). */
    public setCaptionStyle(style: CaptionStyle): void {
        const field = this.getCurrentField();
        if (!field?._id) return;

        this.editItemsService.updateItemConfig(
            field._id,
            withCaptionStyle(field.configs, style, this.editDataService.document()?.config)
        );
    }

    /** Descrição ao lado pelo menu "Foto" (igual ao documento vira herança). */
    public setCaptionSide(side: boolean): void {
        const field = this.getCurrentField();
        if (!field?._id) return;

        this.editItemsService.updateItemConfig(
            field._id,
            withCaptionSide(field.configs, side, this.editDataService.document()?.config)
        );
    }

    /** "Voltar ao padrão do documento": só as chaves da foto; o estilo do texto fica. */
    public restoreLook(): void {
        const field = this.getCurrentField();
        if (!field?._id) return;

        this.editItemsService.updateItemConfig(
            field._id,
            withoutKeys(field.configs, ImageCardComponent.LOOK_KEYS)
        );
    }

    /**
     * Cantos da foto: arrastar muda a largura (a altura acompanha, na proporção da imagem
     * grande). A foto é centralizada, então a largura é o dobro da distância do ponteiro
     * até o eixo da faixa, qualquer que seja o canto. Perto de um tamanho pronto, gruda nele.
     */
    public startResize(event: PointerEvent): void {
        if (event.button !== 0 || this.editDataService.documentIsBlocked()) return;
        event.preventDefault();
        event.stopPropagation();

        const handle = event.currentTarget as HTMLElement;
        const band = handle.closest('.container-image') as HTMLElement | null;
        const figure = handle.closest('.media-figure') as HTMLElement | null;
        if (!band) return;
        handle.setPointerCapture(event.pointerId);

        const rect = band.getBoundingClientRect();
        const center = rect.left + rect.width / 2;
        let sizeAt = (clientX: number): ImageSize =>
            snapImageSize((Math.abs(clientX - center) * 2 * 100) / rect.width);

        // Ao lado da legenda a foto fica presa à esquerda: a largura é a distância do ponteiro
        // até a borda esquerda da coluna (a área útil da .media-figure, sem o padding do
        // cartão), até 60%, como no PDF.
        if (this.captionSide() && figure) {
            const box = figure.getBoundingClientRect();
            const style = getComputedStyle(figure);
            const left = box.left + parseFloat(style.paddingLeft || '0');
            const width = box.width - parseFloat(style.paddingLeft || '0') - parseFloat(style.paddingRight || '0');
            sizeAt = (clientX: number): ImageSize =>
                snapSideImageSize(((clientX - left) * 100) / width);
        }

        this.resizing.set(true);
        this._liveSize.set(sizeAt(event.clientX));

        const move = (e: PointerEvent): void => this._liveSize.set(sizeAt(e.clientX));
        const end = (e: PointerEvent): void => {
            handle.removeEventListener('pointermove', move);
            handle.removeEventListener('pointerup', end);
            handle.removeEventListener('pointercancel', end);

            const size = this._liveSize();
            if (size && e.type !== 'pointercancel') this.chooseSize(size);
            this._liveSize.set(null);
            this.resizing.set(false);
        };

        handle.addEventListener('pointermove', move);
        handle.addEventListener('pointerup', end);
        handle.addEventListener('pointercancel', end);
    }

    /**
     * Retorna o texto simples da descrição (extrai do formato Delta se necessário).
     * A description vem como Quill Delta; sem achatar, o alt/title exibiria o JSON cru
     * ("insert", aspas, chaves).
     */
    public getDescriptionText(): string {
        const field = this.getCurrentField();
        const plainText = deltaToPlainText(field.description);

        return plainText || this._translate.instant('document.edit.imageCard.defaultAlt');
    }

    /**
     * Abre a tela cheia com TODAS as imagens da seção, posicionada nesta —
     * navegação entre elas igual ao preview. Fora do editor (lixeira/favoritos)
     * não há seção: cai para a imagem única.
     */
    public openFullScreen(): void {
        const field = this.getCurrentField();

        if (this.type() === CardTypes.DEFAULT) {
            const { images, startIndex } = buildSectionGallery(
                this.editDataService.currentSession()?.items ?? [],
                field._id,
                0,
                this._translate.instant('document.edit.imageCard.defaultAlt')
            );

            if (images.length > 0) {
                this.galleryImages.set(images);
                this.galleryStartIndex.set(startIndex);
                this.showFullScreen.set(true);
                return;
            }
        }

        const descriptionText = this.getDescriptionText();
        this.galleryImages.set([
            { url: field.value, alt: descriptionText, title: descriptionText },
        ]);
        this.galleryStartIndex.set(0);
        this.showFullScreen.set(true);
    }

    /**
     * Manipula o evento quando a(s) imagem(ns) é(são) salva(s)
     * @param editedImages Lista de imagens editadas com position, file e size
     */
    public onImageSaved(editedImages: { position: number; file: File; size: number }[]): void {
        // Fecha o modal
        this.showEditImage.set(false);

        // Se não há imagens editadas, não faz nada
        if (!editedImages || editedImages.length === 0) return;

        // Para compatibilidade com o modo único, pega a primeira imagem editada
        // (no modo único sempre será apenas uma imagem)
        const editedImage = editedImages[0];

        // Gera ID único para o upload
        const uploadId = this._generateUploadId('image-edit');

        // Adiciona o upload à lista de progresso
        this._uploadProgressService.addUpload(uploadId, editedImage.file.name, editedImage.size);

        // 1- Solicita a URL de upload para o documento
        this._documentController
            .generateUrlsToUploads(this.editDataService.getDocumentId(), [
                {
                    mimeType: editedImage.file.type,
                    size: editedImage.size,
                },
            ])
            .pipe(
                // 2- Realiza o upload da nova imagem com progresso
                switchMap((urls: string[]) =>
                    this._digitalOceanStorage.uploadToSignedUrlWithProgress(
                        editedImage.file,
                        urls[0],
                        progress => {
                            this._uploadProgressService.updateProgress(uploadId, progress);
                        }
                    )
                ),
                // 3- Troca a imagem no backend
                switchMap((newUrl: string) => {
                    const field = this.getCurrentField();
                    return this._documentFieldsController.changeImageField(
                        this.editDataService.getDocumentId(),
                        this.editDataService.getCurrentSessionId(),
                        field._id,
                        newUrl,
                        editedImage.size
                    );
                })
            )
            .subscribe({
                next: () => {
                    this._uploadProgressService.completeUpload(uploadId);
                },
                error: error => {
                    this._uploadProgressService.errorUpload(
                        uploadId,
                        error.message ||
                            this._translate.instant('document.edit.imageCard.uploadImageError')
                    );
                },
            });
    }

    /**
     * Adiciona imagens
     * @returns
     */
    private changeImage(): void {
        const items: {
            index: number;
            file: File;
            mimeType: string;
            size: number;
            url?: string;
        }[] = [];

        let uploadId: string | null = null;

        // 1- Busca as imagens da galeria do PC, e comprimi as imagens
        this._mediaUtils
            .selectImagesFromGallery()
            .pipe(
                // 2- Envia os dados para o backend gerar as URLs do upload
                switchMap(images => {
                    if (images.length === 0) {
                        // Usuário cancelou a operação, não continua o fluxo
                        return of(null);
                    }

                    images.forEach((image, index) => {
                        items.push({
                            index: index,
                            file: image,
                            mimeType: image.type,
                            size: this._mediaUtils.getFileSizeInGB(image),
                        });
                    });

                    // Gera ID único para o upload
                    uploadId = this._generateUploadId('image-swap');

                    // Adiciona o upload à lista de progresso
                    this._uploadProgressService.addUpload(
                        uploadId,
                        items[0].file.name,
                        items[0].size
                    );

                    return this._documentController
                        .generateUrlsToUploads(this.editDataService.getDocumentId(), items)
                        .pipe(
                            switchMap(urls => {
                                // 3- Envia para realizar o upload das mídias com progresso
                                return this._digitalOceanStorage.uploadToSignedUrlWithProgress(
                                    items[0].file,
                                    urls[0],
                                    progress => {
                                        if (uploadId) {
                                            this._uploadProgressService.updateProgress(
                                                uploadId,
                                                progress
                                            );
                                        }
                                    }
                                );
                            }),
                            // 4- Altera a imagem no backend
                            switchMap((newUrl: string) => {
                                const field = this.getCurrentField();
                                return this._documentFieldsController.changeImageField(
                                    this.editDataService.getDocumentId(),
                                    this.editDataService.getCurrentSessionId(),
                                    field._id,
                                    newUrl,
                                    this._mediaUtils.getFileSizeInGB(items[0].file)
                                );
                            }),
                            // Marca o upload como completo antes de finalizar
                            tap(() => {
                                if (uploadId) {
                                    this._uploadProgressService.completeUpload(uploadId);
                                }
                            })
                        );
                })
            )
            .subscribe({
                next: () => {
                    // Upload já foi marcado como completo no tap acima
                },
                error: error => {
                    if (uploadId) {
                        this._uploadProgressService.errorUpload(
                            uploadId,
                            error.message ||
                                this._translate.instant('document.edit.imageCard.uploadImageError')
                        );
                    }
                },
            });
    }

    /**
     * Gera um ID único para o upload
     */
    private _generateUploadId(type: string): string {
        return `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Retorna o campo atual
     * @returns DocumentItemPartial | TrashDocumentItemEntity | FavoriteFieldEntity
     */
    public getCurrentField(): DocumentItemPartial {
        switch (this.type()) {
            case 'default':
                return this.field();
            case 'trash':
                return this.trashField().item;
            case 'favorite':
                return this.favoriteField().field;
        }
    }
}
